import { AppButtonIcon } from '@/components/App/AppButtonIcon';
import router from '@/router/router';
import { BrokerService } from '@/services/brokerService';
import { UserStore } from '@/stores';
import { Icons } from '@/utils';
import {
	NButton,
	NCheckbox,
	NForm,
	NFormItem,
	NInput,
	NModal,
	NSelect,
	NSpace,
	useMessage,
} from 'naive-ui';
import { computed, defineComponent, onMounted, ref } from 'vue';

export const AppSupportButton = defineComponent({
	name: 'AppSupportButton',

	setup() {
		const showModal = ref(false);
		const isSubmitting = ref(false);
		const subject = ref('');
		const description = ref('');
		const brokerName = ref('');
		const message = useMessage();

		const SUBJECT_OPTIONS: Array<{ label: string; value: string }> = [
			{ label: 'Plataforma', value: 'plataforma' },
			{ label: 'Propostas (Garantia)', value: 'propostas' },
			{ label: 'Contratos', value: 'contratos' },
			{ label: 'Financeiro', value: 'financeiro' },
			{ label: 'Clientes', value: 'clientes' },
			{ label: 'Imobiliárias', value: 'imobiliarias' },
			{ label: 'Outro', value: 'outro' },
		];

		// Default subject
		subject.value = SUBJECT_OPTIONS[0].value;

		const userStore = UserStore();

		const userInfo = computed(() => ({
			// Firebase user object merged with custom claims
			...userStore.user,
			...userStore.userClaims,
		}));

		const currentRouteInfo = computed(() => {
			const current = router.currentRoute.value;
			return {
				name: String(current.name || ''),
				path: current.path || '',
				fullPath: current.fullPath || '',
			};
		});

		const currentCaseId = computed(() => {
			const params = router.currentRoute.value.params as any;
			return String(params?.caseId || '');
		});

		const canLinkCase = computed(() => !!currentCaseId.value);
		const linkCase = ref(false);

		onMounted(async () => {
			try {
				const bId = userInfo.value.BrokerId as string | undefined;
				if (bId) {
					const resp = await BrokerService.getBroker(bId);
					brokerName.value = resp.data?.fantasyName || resp.data?.name || '';
				}
			} catch (err) {
				// Ignore broker name fetch errors
			}
		});

		const openModal = () => {
			linkCase.value = canLinkCase.value;
			showModal.value = true;
		};

		const closeModal = () => {
			showModal.value = false;
		};

		const submitReport = async () => {
			if (!description.value.trim()) return;
			isSubmitting.value = true;

			const name = String(
				(userInfo.value as any).Name ||
					(userInfo.value as any).displayName ||
					(userInfo.value as any).Email ||
					(userInfo.value as any).email ||
					'-',
			);
			const email = String((userInfo.value as any).Email || (userInfo.value as any).email || '-');
			const userId = String((userInfo.value as any).Id || (userInfo.value as any).uid || '-');
			const broker = String(brokerName.value || '-');
			const brokerId = String((userInfo.value as any).BrokerId || '-');
			const routeName = currentRouteInfo.value.name;
			const routeFullPath = currentRouteInfo.value.fullPath;
			const url = String(window.location.href);
			const userAgent = String(navigator.userAgent);

			// Simplified payload for n8n webhook
			const subjectLabel =
				SUBJECT_OPTIONS.find((o) => o.value === subject.value)?.label ||
				subject.value ||
				'Sem assunto';
			const payload: any = {
				subject: subjectLabel,
				description: description.value.trim(),
				user: {
					id: userId,
					name,
					email,
					brokerId,
					brokerName: broker,
				},
				context: {
					routeName,
					routePath: routeFullPath,
					url,
					userAgent,
				},
			};

			if (canLinkCase.value && linkCase.value) {
				payload.caseId = currentCaseId.value;
			}

			try {
				const endpoint = (import.meta as any).env?.VITE_SUPPORT_WEBHOOK as string | undefined;
				if (!endpoint) {
					message.error('Webhook de suporte não configurado. Defina VITE_SUPPORT_WEBHOOK.');
					return;
				}

				await fetch(endpoint, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(payload),
				});

				message.success('Seu relato foi enviado ao suporte. Obrigado!');
				description.value = '';
				subject.value = '';
				showModal.value = false;
			} catch (error) {
				message.error('Não foi possível enviar seu relato. Tente novamente.');
				console.error('Error sending support report:', error);
			} finally {
				isSubmitting.value = false;
			}
		};

		return () => (
			<>
				<AppButtonIcon icon={Icons.FAQ} onClick={openModal} tooltipMessage="Reportar problema" />

				<NModal
					show={showModal.value}
					onUpdateShow={(v: boolean) => (showModal.value = v)}
					preset="card"
					title="Relatar um problema"
					style={{ maxWidth: '600px' }}
				>
					<NForm labelPlacement="top">
						<NFormItem label="Assunto">
							<NSelect
								v-model:value={subject.value}
								options={SUBJECT_OPTIONS}
								placeholder="Selecione o assunto"
							/>
						</NFormItem>

						<NFormItem label="Descrição do problema" required>
							<NInput
								type="textarea"
								rows={6}
								v-model:value={description.value}
								placeholder="Descreva o que aconteceu"
							/>
						</NFormItem>

						{canLinkCase.value && (
							<NFormItem label="Relacionar ao caso atual?">
								<NCheckbox v-model:checked={linkCase.value}>
									Problema relacionado ao caso (ID: {currentCaseId.value})
								</NCheckbox>
							</NFormItem>
						)}

						<NSpace justify="end">
							<NButton quaternary onClick={closeModal}>
								Cancelar
							</NButton>
							<NButton
								type="primary"
								loading={isSubmitting.value}
								disabled={!description.value.trim()}
								onClick={submitReport}
							>
								Enviar
							</NButton>
						</NSpace>
					</NForm>
				</NModal>
			</>
		);
	},
});

export default AppSupportButton;
